import React, { ReactElement, useEffect, useMemo, useState } from "react";
import { matchSorter } from "match-sorter";
import { useHistory, useLocation } from "react-router-dom";
import {
  Cell,
  Column,
  HeaderGroup,
  PluginHook,
  Row,
  SortingRule,
  TableCellProps,
  TableOptions,
  TableRowProps,
  TableState,
  useAsyncDebounce,
  useExpanded,
  UseExpandedOptions,
  useGlobalFilter,
  UseGlobalFiltersInstanceProps,
  UseGlobalFiltersOptions,
  UseGlobalFiltersState,
  usePagination,
  UsePaginationInstanceProps,
  UsePaginationOptions,
  UsePaginationState,
  useRowSelect,
  useSortBy,
  UseSortByColumnProps,
  useTable,
} from "react-table";
import styled from "styled-components";
import { Button, ButtonGroup, HTMLTable, Icon } from "@blueprintjs/core";
import { faSearch, faSpinner } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Popover } from "@mantine/core";

import { Input } from "@components/Input";
import { LinkButton } from "@components/ThemeButton";
import { useDeploymentFindings, useIsDesktop } from "@hooks";
import { useDebouncedEffect } from "@utils";

import { IndeterminateCheckbox } from "./IndeterminantCheckbox";

import "./DataTable.scss";

type ContainerProps = {
  rowHover?: boolean;
  colorLinks?: boolean;
};

type PaginationProps = {
  pageSize: number;
  disabled?: boolean;
  pageCount?: number;
  manualPagination?: boolean;
};

type SortProps =
  | {
      initialId?: string;
      initialDesc?: boolean;
    }
  | boolean;

export type RowSelectDialogProps<D> = {
  selected: D[];
  selectAll?: boolean; // indicates user has selected all items across all pages
  closeDialog: () => void;
  refresh: () => void;
  showTriageOptions?: boolean;
  availableItems?: D[];
};

/**
 * Props needed for row select.
 * If row select props are passed in to the data table, then check boxes and a bulk
 * action button will appear. That button can either cause a dialog to open (see `RowSelectDialogProps`),
 * or run its own action on click (`onButtonClick`)
 */
type RowSelectProps<D> = {
  Dialog?: React.FC<RowSelectDialogProps<D>>;
  onButtonClick?: (ds: D[]) => void;
  selectEnabled: boolean;
  buttonText: string;
  refreshRows: () => void;
  totalCount?: number;
};

type DataTableProps<D extends object> = {
  columns: Column<D>[];
  data: D[];
  pagination?: PaginationProps;
  filter?: FilterFunction<D>;
  sorting?: SortProps;
  initialState?: Partial<TableState<D>>;
  interactive?: boolean;
  updateUrl?: boolean;
  rowSelect?: RowSelectProps<D>;
  isFullPage?: boolean; // reformat table to be more information-dense
  getTableRowProps?: (row: Row<D>) => Partial<TableRowProps>;
  getRowProps?: (
    row: Row<D>
  ) => Partial<
    React.DetailedHTMLProps<
      React.HTMLAttributes<HTMLTableRowElement>,
      HTMLTableRowElement
    >
  >;
  getTableCellProps?: (cell: Cell<D>) => Partial<TableCellProps>;
  onRowClick?: (datum: D) => void;
  hasMoreButton?: boolean;
  onMoreClick?: () => void;
  onChangePageIndex?: (newPageIndex: number) => void;
  onChangeSorting?: (sortBy: Array<SortingRule<D>>) => void;
  disableRowHover?: boolean;
  colorLinks?: boolean;
};

interface FilterFunction<D extends object> {
  (rows: Row<D>[], ids: string[], filterValue: string): Row<D>[];
}

export function fuzzyTextFilter<R extends { values: Record<string, any> }>(
  rows: R[],
  ids: string[],
  filterValue: string
): R[] {
  return matchSorter(rows, filterValue, {
    keys: ids.map((id) => (row) => row.values[id]),
  });
}
// Empty search means search all
fuzzyTextFilter.autoRemove = (value: string) => value === "";

export const exactTextFilter = <R extends { values: Record<string, any> }>(
  rows: R[],
  ids: string[],
  filterValue: string
): R[] => {
  const lowerFilter = filterValue.toLocaleLowerCase();
  const processed = rows.map((r) =>
    ids.map((id) => `${r.values[id]}`.toLocaleLowerCase())
  );
  return rows.filter(
    (_, ix) => !!processed[ix].find((v) => v.includes(lowerFilter))
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;

  ${(props: ContainerProps) =>
    props.rowHover &&
    `
  /* Mimic .bp3-table-interactive without the pointer */
  & tbody > tr:hover {
    background-color: rgba(var(--gray3-rgb), 0.3) !important;
  }

  `}
  ${(props: ContainerProps) =>
    props.colorLinks &&
    `
  & tbody a {
    color: var(--link-color) !important;
  }
  & tbody a:hover {
    text-decoration: underline !important;
  }
  `}

  & th {
    white-space: nowrap;
  }
`;

const AboveTable = styled.div`
  display: flex;
  justify-content: space-between;
`;

export const PaginationContainer = styled.div`
  margin: ${(props) => props.theme.margin};
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
`;

export const DropshadowBox = styled.div`
  box-shadow: 0 0 0 1px rgb(16 22 26 / 10%), 0 0 0 rgb(16 22 26 / 0%),
    0 1px 1px rgb(16 22 26 / 20%);
  border-radius: 5px;
  display: table;
`;

const infoText = {
  color: "#777777",
  fontWeight: 200,
  fontSize: "var(--global-font-size-xs)",
  marginBottom: "-0.2rem",
};

function Header<D extends object>(
  props: HeaderGroup<D> & { sorting: boolean | undefined }
) {
  const sp = props.sorting
    ? (props as unknown as UseSortByColumnProps<D>)
    : undefined;
  const hp = props.getHeaderProps(sp?.getSortByToggleProps());
  return (
    <th
      {...hp}
      style={{
        fontWeight: "normal",
        cursor: props.sorting ? "pointer" : undefined,
      }}
    >
      {props.render("Header")}
      {sp?.isSorted ? (
        sp?.isSortedDesc ? (
          <Icon icon="caret-down" />
        ) : (
          <Icon icon="caret-up" />
        )
      ) : sp?.canSort ? (
        // invisible icon to prevent columns from changing width
        // as user sorts by different columns
        <Icon icon="double-caret-vertical" color="none" />
      ) : null}
    </th>
  );
}

function Pagination<D extends object>(
  props: UsePaginationInstanceProps<D> & {
    pageIndex: number;
    isFullPage: boolean;
  }
) {
  const location = useLocation();
  const { isFetching, data } = useDeploymentFindings();
  const minimalProps = { margin: "12px" };
  const defaultProps = { margin: "0.6em 0 0 0" };

  const isLoadingFindings = useMemo(() => {
    return (
      location.pathname.includes("/findings") &&
      isFetching &&
      !data?.pages[props.pageIndex]
    );
  }, [isFetching, location.pathname, data?.pages, props.pageIndex]);

  const fetchNextPage = () => {
    // Throttle on the findings page to prevent next page while the data is fetching
    // TODO: This can be removed after the data table rewrite
    if (isLoadingFindings) return;
    props.nextPage();
  };

  return (
    <PaginationContainer theme={props.isFullPage ? defaultProps : minimalProps}>
      <DropshadowBox>
        {props.pageCount > 1 ? (
          <span
            style={{
              ...infoText,
              display: "table-cell",
              verticalAlign: "middle",
              paddingLeft: "1em",
            }}
          >
            {`${props.pageIndex + 1}`} of {`${props.pageCount}`} pages
          </span>
        ) : undefined}
        <ButtonGroup>
          <Button
            onClick={() => props.previousPage()}
            disabled={!props.canPreviousPage}
            icon={"chevron-left"}
            minimal
          />
          <Button
            onClick={fetchNextPage}
            disabled={!props.canNextPage}
            icon={
              isLoadingFindings ? (
                <FontAwesomeIcon icon={faSpinner} spin size="xs" />
              ) : (
                "chevron-right"
              )
            }
            minimal
          />
        </ButtonGroup>
      </DropshadowBox>
    </PaginationContainer>
  );
}

function Filter<D extends object>(
  props: UseGlobalFiltersInstanceProps<D> & {
    filterState: string;
    updateUrl?: boolean;
    isMinimal: boolean;
  }
) {
  const location = useLocation();
  const history = useHistory();
  const { filterState, updateUrl } = props;
  const [value, setValue] = useState<string>(filterState);
  const onUpdate = useAsyncDebounce((value) => {
    props.setGlobalFilter(value || undefined);
  }, 100);
  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
    onUpdate(event.target.value);
  };

  // update URL after filters
  useDebouncedEffect(
    () => {
      if (!updateUrl) {
        return;
      }
      const params = new URLSearchParams(location.search);
      if (value === "") {
        params.delete("q");
      } else {
        params.set("q", value);
      }

      const paramsString = params.toString() ? `?${params.toString()}` : "";
      if (paramsString !== location.search) {
        history.push({ search: params.toString() });
      }
    },
    1000,
    [history, location, value, updateUrl]
  );

  const style: React.CSSProperties = {
    margin: props.isMinimal ? "12px" : "0 0 32px 0",
  };

  return (
    <Input
      onChange={onChange}
      style={style}
      placeholder="Filter..."
      icon={faSearch}
      endText={
        filterState &&
        `Showing ${props.globalFilteredRows.length} / ${props.preGlobalFilteredRows.length} results`
      }
    />
  );
}

const defaultPropGetter = () => ({});

function Table<D extends object>({
  columns: baseColumns,
  data,
  interactive,
  pagination,
  filter,
  updateUrl,
  rowSelect,
  sorting,
  getRowProps: additionalRowProps,
  initialState = {} as Partial<TableState<D>>,
  isFullPage = true,
  getTableRowProps: getRowProps = defaultPropGetter,
  getTableCellProps: getCellProps = defaultPropGetter,
  onRowClick,
  hasMoreButton,
  onMoreClick,
  onChangePageIndex,
  onChangeSorting,
  disableRowHover,
  colorLinks,
}: DataTableProps<D>): ReactElement<any, any> {
  const location = useLocation();
  const columns: Column<D>[] = useMemo(
    () => [
      {
        id: "selection",
        // header can select all rows
        Header: ({ getToggleAllPageRowsSelectedProps }) => (
          <div data-testid="table-header-checkbox">
            <IndeterminateCheckbox
              disabled={!rowSelect?.selectEnabled}
              {...getToggleAllPageRowsSelectedProps()}
            />
          </div>
        ),
        Cell: ({ row }: { row: Row<D> }) => (
          <div
            onClick={(e) => e.stopPropagation()}
            data-testid="table-row-checkbox"
          >
            <IndeterminateCheckbox
              disabled={!rowSelect?.selectEnabled}
              {...row.getToggleRowSelectedProps()}
            />
          </div>
        ),
      },
      ...baseColumns,
    ],
    [baseColumns, rowSelect]
  );

  const [tableOptions, plugins] = useMemo(() => {
    const tableOptions: TableOptions<D> &
      UsePaginationOptions<D> &
      UseGlobalFiltersOptions<D> &
      UseExpandedOptions<D> = {
      columns,
      data,
    };
    const plugins: PluginHook<D>[] = [];
    if (filter !== undefined) {
      const filterTypes = { fuzzy: filter };
      tableOptions.filterTypes = filterTypes;
      tableOptions.globalFilter = "fuzzy";
      plugins.push(useGlobalFilter);
    }
    if (sorting) {
      plugins.push(useSortBy);
    }
    plugins.push(useExpanded);
    if (pagination) {
      plugins.push(usePagination);
    }

    const sortProps =
      typeof sorting === "object" ? sorting : { initialDesc: true };

    let globalFilterInitial = "";
    if (updateUrl) {
      const params = new URLSearchParams(location.search);
      globalFilterInitial = params.get("q") ?? "";
    }

    if (sorting && sortProps.initialId) {
      tableOptions.initialState = {
        ...initialState,
        globalFilter: globalFilterInitial,
        sortBy: [{ id: sortProps.initialId, desc: !!sortProps.initialDesc }],
      } as Partial<TableState<D>>;
    }

    if (pagination?.pageSize) {
      tableOptions.initialState = {
        ...initialState,
        ...(tableOptions.initialState || {}),
        pageSize: pagination.pageSize,
      };
    }

    if (!rowSelect) {
      tableOptions.initialState = {
        ...initialState,
        ...(tableOptions.initialState || {}),
        hiddenColumns: ["selection"],
      };
    }

    // set pagination.manualPagination to true when using cursor pagination
    // Requires pagination.pageCount to also be set
    if (pagination?.manualPagination) {
      tableOptions.pageCount = pagination.pageCount;
      // https://react-table.tanstack.com/docs/faq#how-do-i-stop-my-table-state-from-automatically-resetting-when-my-data-changes
      tableOptions.manualPagination = true;
      tableOptions.autoResetPage = false;
    }

    if (onChangeSorting || onChangePageIndex) {
      tableOptions.stateReducer = (newState, action: any) => {
        // Having onChange in reducer is a bit of a hack, but is is only reliable place i could find the action
        // Putting it to setTimeout(...,0) makes the change outside of rendering phase
        setTimeout(() => {
          if (onChangeSorting && action.type === "toggleSortBy") {
            onChangeSorting(newState.sortBy);
          }
          if (onChangePageIndex && action.type === "gotoPage") {
            onChangePageIndex(newState.pageIndex);
          }
        }, 0);

        return newState;
      };
    }
    return [tableOptions, plugins];
  }, [
    columns,
    data,
    filter,
    pagination,
    sorting,
    initialState,
    updateUrl,
    location,
    onChangeSorting,
    onChangePageIndex,
    rowSelect,
  ]);
  const [dialogIsOpen, setDialogIsOpen] = useState<boolean>(false);
  const [selectAllRowsAllPages, setSelectAllRowsAllPages] =
    useState<boolean>(false);

  const table = useTable(tableOptions, ...plugins, useRowSelect);
  const isDesktop = useIsDesktop();

  const filterProps = filter
    ? (table as unknown as UseGlobalFiltersInstanceProps<D>)
    : undefined;

  const paginationProps = pagination
    ? (table as unknown as UsePaginationInstanceProps<D>)
    : undefined;

  const paginationState = pagination
    ? ((table as any).state as UsePaginationState<D>)
    : undefined;

  const paginationEnabled = pagination && !pagination.disabled;
  const showPagination =
    paginationProps &&
    paginationProps.pageCount > 1 &&
    paginationState &&
    paginationEnabled;

  useEffect(() => {
    if (
      pagination &&
      table.selectedFlatRows.length < pagination.pageSize &&
      selectAllRowsAllPages
    ) {
      setSelectAllRowsAllPages(false);
    }
  }, [
    table.selectedFlatRows,
    selectAllRowsAllPages,
    pagination,
    pagination?.pageSize,
  ]);

  const totalRowCount =
    (rowSelect && rowSelect.totalCount) || table.globalFilteredRows?.length;

  return (
    <Container rowHover={!disableRowHover} colorLinks={!!colorLinks}>
      <AboveTable>
        {filterProps ? (
          <Filter
            filterState={
              (table.state as unknown as UseGlobalFiltersState<D>).globalFilter
            }
            updateUrl={updateUrl}
            isMinimal={!isFullPage}
            {...filterProps}
          />
        ) : undefined}
        {showPagination && !isFullPage ? (
          <Pagination
            {...paginationProps}
            pageCount={pagination?.pageCount || 1}
            pageIndex={paginationState.pageIndex}
            isFullPage={isFullPage}
          />
        ) : undefined}
        {rowSelect?.onButtonClick && (
          <Button
            disabled={Object.keys(table.state.selectedRowIds).length === 0}
            onClick={() =>
              rowSelect.onButtonClick!(
                table.selectedFlatRows.map((r) => r.original)
              )
            }
            intent="primary"
            style={{ marginLeft: "auto", alignSelf: "baseline" }}
          >
            {rowSelect.buttonText}
          </Button>
        )}
      </AboveTable>
      {rowSelect && rowSelect.Dialog && (
        <div className="select-headers">
          {table.selectedFlatRows.length === pagination?.pageSize &&
            totalRowCount > pagination?.pageSize && (
              <div className="select-all-message">
                {selectAllRowsAllPages
                  ? `All ${totalRowCount} items matching these filters are selected.`
                  : `The ${pagination?.pageSize} items on this page are selected.`}
                <LinkButton
                  data-testid="select-all-button"
                  onClick={() =>
                    setSelectAllRowsAllPages((current) => !current)
                  }
                >
                  {selectAllRowsAllPages
                    ? `Select items on this page only`
                    : `Select all ${totalRowCount} items`}
                </LinkButton>
              </div>
            )}
          <Popover
            opened={dialogIsOpen}
            onChange={setDialogIsOpen}
            withinPortal
            position="right"
            shadow="md"
          >
            <Popover.Target>
              <div style={{ marginLeft: "auto" }}>
                <Button
                  intent="primary"
                  disabled={
                    Object.keys(table.state.selectedRowIds).length === 0
                  }
                  onClick={() => setDialogIsOpen(!dialogIsOpen)}
                  data-testid="triage-button"
                >
                  {`${rowSelect.buttonText} (${
                    selectAllRowsAllPages
                      ? totalRowCount
                      : table.selectedFlatRows.length
                  })`}
                </Button>
              </div>
            </Popover.Target>
            <Popover.Dropdown>
              <rowSelect.Dialog
                selected={table.selectedFlatRows.map((r) => r.original)}
                selectAll={selectAllRowsAllPages}
                closeDialog={() => setDialogIsOpen(false)}
                refresh={rowSelect.refreshRows}
                availableItems={(
                  table.globalFilteredRows ||
                  table.preGlobalFilteredRows ||
                  []
                ).map((row) => row.original)}
              />
            </Popover.Dropdown>
          </Popover>
        </div>
      )}
      <HTMLTable
        {...table.getTableProps()}
        style={{
          width: "100%",
          fontSize: isDesktop
            ? "var(--global-font-size-s)"
            : "var(--global-font-size-xs",
        }}
        condensed
        striped
        interactive={!!interactive}
      >
        <thead>
          {table.headerGroups.map((hg) => (
            <tr {...hg.getHeaderGroupProps()}>
              {hg.headers.map((column) => (
                <Header
                  key={column.id}
                  sorting={!!sorting || typeof sorting === "object"}
                  {...column}
                />
              ))}
            </tr>
          ))}
        </thead>
        <tbody // nosemgrep: typescript.react.security.audit.react-html-element-spreading.react-html-element-spreading
          {...table.getTableBodyProps()}
          // we can trust the props coming out of getTableBodyProps()
        >
          {(paginationProps?.page ?? table.rows).map((row) => {
            table.prepareRow(row);
            const extraRowProps = additionalRowProps
              ? additionalRowProps(row)
              : {};
            return (
              <tr // nosemgrep: typescript.react.security.audit.react-html-element-spreading.react-html-element-spreading
                // false positives here, we don't need to worry about the contents of extraRowProps and getRowProps
                onClick={(_) => onRowClick && onRowClick(row.original)}
                {...extraRowProps}
                {...row.getRowProps(getRowProps(row))}
                style={{ cursor: onRowClick ? "pointer" : undefined }}
              >
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps(getCellProps(cell))}>
                    {cell.render("Cell")}
                  </td>
                ))}
              </tr>
            );
          })}
          {hasMoreButton && (
            <tr onClick={onMoreClick}>
              <td colSpan={columns.length}>More &#8595;</td>
            </tr>
          )}
        </tbody>
      </HTMLTable>
      {showPagination && isFullPage ? (
        <Pagination
          {...paginationProps}
          pageIndex={paginationState.pageIndex}
          isFullPage={isFullPage}
        />
      ) : undefined}
    </Container>
  );
}

export const DataTable = Table;
